import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { betCodeAPI } from '../../api/endpoints';
import { formatTimeAgo } from '../../utils/helpers';
import { FaUser, FaReply, FaTimes } from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../common/LoadingSpinner';
import './CommentSection.css';

const CommentSection = ({ betCodeId }) => {
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchComments();
  }, [betCodeId]);

  const fetchComments = async () => {
    setLoading(true);
    try {
      const response = await betCodeAPI.getComments(betCodeId);
      setComments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    if (!isAuthenticated) {
      toast.error('Please login to comment');
      return;
    }

    setSubmitting(true);
    try {
      await betCodeAPI.comment(betCodeId, { comment: newComment.trim() });
      toast.success('Comment posted!');
      setNewComment('');
      fetchComments();
    } catch (error) {
      toast.error('Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    if (!replyContent.trim() || !replyTo) {
      toast.error('Please enter a reply');
      return;
    }

    if (!isAuthenticated) {
      toast.error('Please login to reply');
      return;
    }

    setSubmitting(true);
    try {
      await betCodeAPI.reply(betCodeId, { 
        parent_id: replyTo,
        content: replyContent.trim()
      });
      toast.success('Reply posted!');
      setReplyContent('');
      setReplyTo(null);
      fetchComments();
    } catch (error) {
      toast.error('Failed to post reply');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="comment-loading">
        <LoadingSpinner size="small" />
      </div>
    );
  }

  return (
    <div className="comment-section">
      {/* Comment Form */}
      {isAuthenticated && (
        <form onSubmit={handleSubmitComment} className="comment-form">
          <div className="comment-input-wrapper">
            <input
              type="text"
              placeholder="Write a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              className="comment-input"
            />
            <button type="submit" className="comment-submit" disabled={submitting}>
              {submitting ? <LoadingSpinner size="small" /> : 'Post'}
            </button>
          </div>
        </form>
      )}

      {/* Reply Form */}
      {replyTo && (
        <div className="reply-form">
          <div className="reply-header">
            <span>Replying to comment</span>
            <button onClick={() => setReplyTo(null)} className="reply-cancel">
              <FaTimes />
            </button>
          </div>
          <form onSubmit={handleSubmitReply} className="reply-input-wrapper">
            <input
              type="text"
              placeholder="Write your reply..."
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              className="reply-input"
            />
            <button type="submit" className="reply-submit" disabled={submitting}>
              {submitting ? <LoadingSpinner size="small" /> : 'Reply'}
            </button>
          </form>
        </div>
      )}

      {/* Comments List */}
      <div className="comments-list">
        {comments.length === 0 ? (
          <div className="no-comments">
            <p>No comments yet. Be the first to comment!</p>
          </div>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="comment-item">
              <div className="comment-avatar">
                {comment.avatar ? (
                  <img src={comment.avatar} alt={comment.author} />
                ) : (
                  <div className="avatar-placeholder">
                    <FaUser />
                  </div>
                )}
              </div>
              <div className="comment-content">
                <div className="comment-header">
                  <span className="comment-author">{comment.author}</span>
                  <span className="comment-time">{formatTimeAgo(comment.created_at)}</span>
                </div>
                <p className="comment-text">{comment.content}</p>
                {isAuthenticated && (
                  <button 
                    className="reply-btn"
                    onClick={() => setReplyTo(comment.id)}
                  >
                    <FaReply /> Reply
                  </button>
                )}
                {comment.replies && comment.replies.length > 0 && (
                  <div className="replies-section">
                    {comment.replies.map((reply) => (
                      <div key={reply.id} className="reply-item">
                        <div className="reply-avatar">
                          {reply.avatar ? (
                            <img src={reply.avatar} alt={reply.author} />
                          ) : (
                            <div className="avatar-placeholder small">
                              <FaUser />
                            </div>
                          )}
                        </div>
                        <div className="reply-content">
                          <div className="reply-header">
                            <span className="reply-author">{reply.author}</span>
                            <span className="reply-time">{formatTimeAgo(reply.created_at)}</span>
                          </div>
                          <p className="reply-text">{reply.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CommentSection;